// @flow
import React, { useEffect, useState, useRef } from 'react';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import LinearProgress from '@mui/material/LinearProgress';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';

import { useTheme } from '@mui/material/styles';
import { createClasses } from '../../../utils/stylesUtils';
import { auth } from '../../../services/firebase';
import { useGetImportPresignMutation, useStartImportMutation } from '../../../features/api/tenantsApi';
import { formatFileSize } from '../csvTemplateUtils';
import uploadingViewStyles from './UploadingView.Style';

type ParseResult = {
  totalRows: number,
  headers: Array<string>,
  sampleRows: Array<Object>,
  suggestedMapping: Object
};

type Props = {
  file: File,
  onParseComplete: (parseResult: ParseResult, jobId: string) => void,
  onError: (error: string) => void,
  onCancel: () => void
};

type UploadStage = 'presign' | 'uploading' | 'parsing' | 'error';

const UploadingView = ({ file, onParseComplete, onError, onCancel }: Props) => {
  const theme = useTheme();
  const classes = createClasses(uploadingViewStyles, theme);

  const [stage, setStage] = useState<UploadStage>('presign');
  const [progress, setProgress] = useState<number>(0);
  const [statusText, setStatusText] = useState<string>('Preparing upload...');
  const [jobId, setJobId] = useState<string | null>(null);

  const abortControllerRef = useRef<?AbortController>(null);
  const hasStartedRef = useRef<boolean>(false);

  const [getImportPresign] = useGetImportPresignMutation();
  const [startImport] = useStartImportMutation();

  useEffect(() => {
    // Prevent double execution in React Strict Mode
    if (hasStartedRef.current) return;
    hasStartedRef.current = true;

    const uploadFile = async () => {
      const firebaseUser = auth.currentUser;
      if (!firebaseUser) {
        onError('Authentication session expired. Please log in again.');
        return;
      }

      try {
        // Step 1: Get presigned URL
        setStage('presign');
        setStatusText('Getting upload URL...');
        setProgress(5);

        const presignResult = await getImportPresign({
          fileName: file.name,
          user: firebaseUser
        }).unwrap();

        const { jobId: newJobId, presignedUrl } = presignResult;
        setJobId(newJobId);
        setProgress(10);

        // Step 2: Upload to S3
        setStage('uploading');
        setStatusText('Uploading file...');

        await uploadToS3(presignedUrl, file, (uploadProgress) => {
          // Upload progress is 10% to 70%
          setProgress(10 + (uploadProgress * 0.6));
        });

        setProgress(70);

        // Step 3: Parse CSV
        setStage('parsing');
        setStatusText('Processing file...');

        const parseResult = await startImport({
          jobId: newJobId,
          user: firebaseUser
        }).unwrap();

        setProgress(100);
        setStatusText('Ready for mapping!');

        // Small delay before moving to next step
        setTimeout(() => {
          onParseComplete(parseResult, newJobId);
        }, 500);

      } catch (error) {
        console.error('[UploadingView] Upload error:', error);
        setStage('error');

        const errorMessage = error.data?.message ||
          error.message ||
          'An error occurred during upload. Please try again.';

        onError(errorMessage);
      }
    };

    uploadFile();

    // Cleanup
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [file, getImportPresign, startImport, onParseComplete, onError]);

  /**
   * Upload file to S3 using XMLHttpRequest for progress tracking
   */
  const uploadToS3 = (
    presignedUrl: string,
    fileToUpload: File,
    onProgress: (progress: number) => void
  ): Promise<void> => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      abortControllerRef.current = new AbortController();

      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const percentComplete = event.loaded / event.total;
          onProgress(percentComplete);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(new Error(`Upload failed with status ${xhr.status}`));
        }
      });

      xhr.addEventListener('error', () => {
        reject(new Error('Network error during upload'));
      });

      xhr.addEventListener('abort', () => {
        reject(new Error('Upload cancelled'));
      });

      xhr.open('PUT', presignedUrl);
      xhr.setRequestHeader('Content-Type', 'text/csv');
      xhr.send(fileToUpload);

      // Handle abort signal
      abortControllerRef.current.signal.addEventListener('abort', () => {
        xhr.abort();
      });
    });
  };

  const handleCancel = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    onCancel();
  };

  const getStageIcon = () => {
    if (stage === 'error') {
      return null;
    }
    return <CircularProgress size={24} sx={classes.spinner} />;
  };

  return (
    <Box sx={classes.container}>
      <Box sx={classes.content}>
        <Box sx={classes.header}>
          {getStageIcon()}
          <Typography variant="h6" sx={classes.title}>
            {stage === 'error' ? 'Upload Failed' : 'Uploading Customer List'}
          </Typography>
        </Box>

        <Box sx={classes.fileInfo}>
          <Typography variant="body2" sx={classes.fileName}>
            {file.name}
          </Typography>
          <Typography variant="caption" sx={classes.fileSize}>
            {formatFileSize(file.size)}
          </Typography>
        </Box>

        <Box sx={classes.progressContainer}>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={classes.progressBar}
          />
          <Box sx={classes.progressInfo}>
            <Typography variant="body2" sx={classes.statusText}>
              {statusText}
            </Typography>
            <Typography variant="body2" sx={classes.progressPercent}>
              {Math.round(progress)}%
            </Typography>
          </Box>
        </Box>

        <Box sx={classes.stages}>
          <Box sx={{
            ...classes.stage,
            ...(stage === 'presign' ? classes.stageActive : {}),
            ...(progress > 10 ? classes.stageComplete : {})
          }}>
            <Box sx={classes.stageDot} />
            <Typography variant="caption">Preparing</Typography>
          </Box>
          <Box sx={classes.stageLine} />
          <Box sx={{
            ...classes.stage,
            ...(stage === 'uploading' ? classes.stageActive : {}),
            ...(progress >= 70 ? classes.stageComplete : {})
          }}>
            <Box sx={classes.stageDot} />
            <Typography variant="caption">Uploading</Typography>
          </Box>
          <Box sx={classes.stageLine} />
          <Box sx={{
            ...classes.stage,
            ...(stage === 'parsing' ? classes.stageActive : {}),
            ...(progress >= 100 ? classes.stageComplete : {})
          }}>
            <Box sx={classes.stageDot} />
            <Typography variant="caption">Processing</Typography>
          </Box>
        </Box>
      </Box>

      <Box sx={classes.actions}>
        <Button
          variant="outlined"
          onClick={handleCancel}
          disabled={stage === 'parsing' && progress > 90}
        >
          Cancel
        </Button>
      </Box>
    </Box>
  );
};

export default UploadingView;
